import { Component } from '@theme/component';

function initCollectionDescriptionToggles(root = document) {
  const toggleSelector = '.desc-show-btn, #desc-show-btn, [data-description-toggle], .see-more, .read-more, .show-more';
  const toggleButtons = [
    ...(root instanceof HTMLElement && root.matches(toggleSelector) ? [root] : []),
    ...root.querySelectorAll(toggleSelector),
  ];

  for (const toggleButton of toggleButtons) {
    if (!(toggleButton instanceof HTMLElement)) continue;
    if (toggleButton.dataset.collectionDescriptionToggleInitialized === 'true') continue;

    const container = toggleButton.closest('.custom-description, .desc-content, .collection-description, .rte') || root;
    const extraContent =
      container.querySelector('.extra-content, [data-description-extra]') ||
      root.querySelector('.extra-content, [data-description-extra]') ||
      document.querySelector('.extra-content, [data-description-extra]');

    if (!(extraContent instanceof HTMLElement)) continue;

    const initialLabel = toggleButton.textContent?.trim() || 'Show More';
    const lowerInitialLabel = initialLabel.toLowerCase();
    const moreLabel = toggleButton.dataset.moreLabel || toggleButton.dataset.moreText || initialLabel;
    let lessLabel = toggleButton.dataset.lessLabel || toggleButton.dataset.lessText;

    if (!lessLabel) {
      if (lowerInitialLabel.includes('show')) {
        lessLabel = 'Show Less';
      } else if (lowerInitialLabel.includes('read')) {
        lessLabel = 'Read Less';
      } else {
        lessLabel = 'See Less';
      }
    }

    const setExpanded = (expanded) => {
      extraContent.classList.toggle('show', expanded);
      extraContent.toggleAttribute('hidden', !expanded);
      toggleButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      toggleButton.textContent = expanded ? lessLabel : moreLabel;
    };

    toggleButton.dataset.collectionDescriptionToggleInitialized = 'true';
    toggleButton.removeAttribute('onclick');
    toggleButton.setAttribute('type', toggleButton.getAttribute('type') || 'button');
    setExpanded(extraContent.classList.contains('show'));

    toggleButton.addEventListener('click', (event) => {
      event.preventDefault();
      setExpanded(!extraContent.classList.contains('show'));
    });
  }
}

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.#initCollectionDescriptionToggle();
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * Supports migrated Porto collection descriptions that already include their
   * own "See more" / "See less" markup in Shopify Admin.
   */
  #initCollectionDescriptionToggle() {
    initCollectionDescriptionToggles(this);
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCollectionDescriptionToggles(), { once: true });
} else {
  initCollectionDescriptionToggles();
}

const collectionDescriptionObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;
      if (
        node.matches('.desc-show-btn, #desc-show-btn, [data-description-toggle], .see-more, .read-more, .show-more') ||
        node.querySelector('.desc-show-btn, #desc-show-btn, [data-description-toggle], .see-more, .read-more, .show-more')
      ) {
        initCollectionDescriptionToggles(node);
      }
    }
  }
});

collectionDescriptionObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
});
